import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../../shared/utils/firebase';
import { InlineLoadingSpinner } from '../../../shared/components';

interface EmailRecipient {
  type: 'to' | 'cc' | 'bcc';
  email: string;
}

const EmailTestPanel: React.FC = () => {
  const [recipients, setRecipients] = useState<EmailRecipient[]>([
    { type: 'to', email: '' }
  ]);
  const [newEmail, setNewEmail] = useState('');
  const [selectedType, setSelectedType] = useState<'to' | 'cc' | 'bcc'>('to');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(true);

  const functions = getFunctions(app);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const addRecipient = () => {
    if (newEmail && validateEmail(newEmail)) {
      setRecipients([...recipients, { type: selectedType, email: newEmail }]);
      setNewEmail('');
      setError(null);
    } else {
      setError('Please enter a valid email address');
    }
  };

  const removeRecipient = (index: number) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleTestEmail = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Group recipients by type
    const toRecipients = recipients.filter(r => r.type === 'to').map(r => r.email);
    const ccRecipients = recipients.filter(r => r.type === 'cc').map(r => r.email);
    const bccRecipients = recipients.filter(r => r.type === 'bcc').map(r => r.email);

    if (toRecipients.length === 0) {
      setError('At least one "To" recipient is required');
      setLoading(false);
      return;
    }

    try {
      if (testMode) {
        // Call a test version that sends to specified recipients
        const triggerTestEmail = httpsCallable(functions, 'triggerWeeklyManagerSummariesTest');
        const result = await triggerTestEmail({
          testRecipients: {
            to: toRecipients,
            cc: ccRecipients,
            bcc: bccRecipients
          }
        });
        
        console.log('Test email result:', result.data);
        setSuccess(`Test email sent successfully to ${toRecipients.join(', ')}`);
      } else {
        // Call the actual function that sends to all managers
        const triggerWeeklySummaries = httpsCallable(functions, 'triggerWeeklyManagerSummaries');
        const result = await triggerWeeklySummaries();
        
        console.log('Email result:', result.data);
        const data = result.data as { results: Array<{ email: string; status: string }> };
        const successCount = data.results.filter(r => r.status === 'sent').length;
        const failureCount = data.results.filter(r => r.status === 'failed').length;
        
        setSuccess(`Emails sent: ${successCount} successful, ${failureCount} failed`);
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      setError(error.message || 'Failed to send email. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={<EmailIcon />}
        title="Weekly Manager Summary Email Test"
        subheader="Test the weekly email functionality with custom recipients"
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This tool allows you to test the weekly manager summary email. In test mode, 
            it will send a sample email with real data to your specified recipients only.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Test Mode
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant={testMode ? "contained" : "outlined"}
              size="small"
              onClick={() => setTestMode(true)}
            >
              Test Recipients Only
            </Button>
            <Button
              variant={!testMode ? "contained" : "outlined"}
              size="small"
              onClick={() => setTestMode(false)}
              color="warning"
            >
              Send to All Managers
            </Button>
          </Stack>
        </Box>

        {testMode && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Add Recipients
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  select
                  size="small"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as 'to' | 'cc' | 'bcc')}
                  SelectProps={{ native: true }}
                  sx={{ width: 100 }}
                >
                  <option value="to">To</option>
                  <option value="cc">CC</option>
                  <option value="bcc">BCC</option>
                </TextField>
                <TextField
                  size="small"
                  placeholder="Email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={addRecipient}
                  disabled={!newEmail}
                >
                  Add
                </Button>
              </Stack>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recipients:
                </Typography>
                {recipients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recipients added yet
                  </Typography>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {recipients.map((recipient, index) => (
                      <Chip
                        key={index}
                        label={`${recipient.type.toUpperCase()}: ${recipient.email}`}
                        onDelete={() => removeRecipient(index)}
                        color={
                          recipient.type === 'to' ? 'primary' : 
                          recipient.type === 'cc' ? 'secondary' : 
                          'default'
                        }
                        size="small"
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Default recipients will be replaced:</strong>
                <br />
                • CC: chris@ufit.com.sg, declan@ufit.com.sg → Your specified CC recipients
                <br />
                • BCC: kyle@ufit.com.sg → Your specified BCC recipients
              </Typography>
            </Alert>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <InlineLoadingSpinner size={20} /> : <SendIcon />}
            onClick={handleTestEmail}
            disabled={loading || (testMode && recipients.filter(r => r.type === 'to').length === 0)}
          >
            {loading ? 'Sending...' : testMode ? 'Send Test Email' : 'Send to All Managers'}
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Email Content:</strong> The email will include:
              <br />• Ignition Client Alerts (matching Dashboard logic)
              <br />• Outstanding Check-Ins
              <br />• Outstanding Success Updates  
              <br />• Pending Reports
              <br />
              <br />
              <strong>Note:</strong> Test emails use real data but only send to your specified recipients.
            </Typography>
          </Alert>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailTestPanel;